import { useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../services/supabase'
import { LEGAL_ENABLED, LEGAL_URLS, LEGAL_VERSION } from '../legal'
import './LegalGate.css'

/**
 * Patronun yasal metinleri onaylaması, kayıtta onay kutusu yokken açılmış
 * kurumlar (Emirler) ve metin sürümü değiştiğinde herkes için.
 *
 * Yalnızca patron: sözleşmenin tarafı kurum, onu da patron temsil ediyor.
 * Kasa ve garson için kişisel verilerin sorumlusu kurumun kendisi.
 *
 * Sorgu başarısız olursa (internet yok) pencere açılmıyor: kasa bağlantı
 * kesildiğinde de çalışmalı, onay bir sonraki bağlantıda sorulur.
 */
export function LegalGate() {
  const { admin, logout } = useAuth()
  const [needed, setNeeded] = useState(false)
  const [termsOk, setTermsOk] = useState(false)
  const [kvkkOk, setKvkkOk] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isOwner = admin?.role === 'owner'
  const uid = admin?.uid

  useEffect(() => {
    if (!LEGAL_ENABLED || !isOwner || !uid || !supabase) return
    let alive = true
    void supabase
      .from('legal_acceptances')
      .select('id')
      .eq('user_id', uid)
      .eq('version', LEGAL_VERSION)
      .limit(1)
      .then(({ data, error }) => {
        if (!alive || error) return
        setNeeded((data ?? []).length === 0)
      })
    return () => {
      alive = false
    }
  }, [isOwner, uid])

  if (!needed) return null

  async function onAccept() {
    if (!supabase) return
    setBusy(true)
    setError(null)
    const { error } = await supabase.rpc('accept_legal', {
      v: LEGAL_VERSION,
      ua: navigator.userAgent,
    })
    setBusy(false)
    if (error) {
      setError('Kaydedilemedi. Bağlantınızı kontrol edip tekrar deneyin.')
      return
    }
    setNeeded(false)
  }

  return (
    <div className="legal" role="dialog" aria-modal="true" aria-labelledby="legal-title">
      <div className="legal__card">
        <h2 id="legal-title">Kullanım koşulları</h2>
        <p className="legal__lead">
          Adissoo’yu kullanmaya devam etmek için metinlerimizi onaylamanız
          gerekiyor. Bir kez sorulur; metinler değişirse yeniden sorulur.
        </p>

        <label className="legal__check">
          <input type="checkbox" checked={termsOk} onChange={(e) => setTermsOk(e.target.checked)} />
          <span>
            <a href={LEGAL_URLS.terms} target="_blank" rel="noreferrer">Kullanıcı Sözleşmesi</a>’ni ve{' '}
            <a href={LEGAL_URLS.privacy} target="_blank" rel="noreferrer">Gizlilik Politikası</a>’nı
            okudum, kabul ediyorum.
          </span>
        </label>
        <label className="legal__check">
          <input type="checkbox" checked={kvkkOk} onChange={(e) => setKvkkOk(e.target.checked)} />
          <span>
            <a href={LEGAL_URLS.kvkk} target="_blank" rel="noreferrer">KVKK Aydınlatma Metni</a>’ni okudum.
          </span>
        </label>

        {error && <p className="legal__error" role="alert">{error}</p>}

        <div className="legal__buttons">
          <button type="button" className="btn btn--ghost" onClick={() => void logout()}>
            Çıkış yap
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!termsOk || !kvkkOk || busy}
            onClick={() => void onAccept()}
          >
            {busy ? 'Kaydediliyor…' : 'Onayla ve devam et'}
          </button>
        </div>
      </div>
    </div>
  )
}
